import { useEffect, useState } from "react";
import { db } from "./firebase";
import { doc, updateDoc, collection, addDoc, onSnapshot, query, where } from "firebase/firestore";
import { inputStyle, btnStyle, btnSecondaryStyle, cardStyle, colors } from "./lib/theme";
import { splitIntoGroups, doublesRoundRobin } from "./lib/roundRobin";
import { computeStandings } from "./lib/stats";

export default function LeagueSession({ sessionId, live }) {
  var players = live.players || [];
  var names = live.playerNames || {};
  var groups = live.groups;

  var [rankBy, setRankBy] = useState("allTime");
  var [history, setHistory] = useState([]);
  var [scoreInputs, setScoreInputs] = useState({}); // court -> [a,b]

  useEffect(function () {
    // League standings/grouping only ever consider league-mode results —
    // social results are recorded but never feed into league ranking.
    var q = rankBy === "session"
      ? query(collection(db, "matches"), where("mode", "==", "league"), where("templateId", "==", live.templateId))
      : query(collection(db, "matches"), where("mode", "==", "league"));
    var unsub = onSnapshot(q, function (snap) {
      setHistory(snap.docs.map(function (d) { return d.data(); }));
    });
    return unsub;
  }, [rankBy, live.templateId]);

  function name(uid) { return names[uid] || uid; }
  function teamName(team) { return team.map(name).join(" & "); }

  function generateGroups() {
    var standings = computeStandings(history, players, names);
    var rankedIds = standings.map(function (s) { return s.uid; });
    var grouped = splitIntoGroups(rankedIds, live.courtCount);
    var newGroups = grouped.map(function (groupIds, i) {
      return {
        court: i + 1,
        players: groupIds,
        matchOrder: doublesRoundRobin(groupIds),
        matchIndex: 0,
        results: [], // { teamA, teamB, winningTeam, scoreA, scoreB }
      };
    });
    updateDoc(doc(db, "liveSessions", sessionId), { groups: newGroups, rankBy: rankBy });
  }

  function submitMatch(group) {
    var m = group.matchOrder[group.matchIndex];
    var s = scoreInputs[group.court] || [0, 0];
    var sA = parseInt(s[0], 10) || 0;
    var sB = parseInt(s[1], 10) || 0;
    var winningTeam = sA === sB ? null : (sA > sB ? "A" : "B");

    addDoc(collection(db, "matches"), {
      sessionId: sessionId,
      templateId: live.templateId,
      occurrenceDate: live.occurrenceDate,
      venueId: live.venueId,
      venueName: live.venueName,
      mode: "league",
      court: group.court,
      teamA: m.teamA,
      teamB: m.teamB,
      players: m.teamA.concat(m.teamB),
      scoreA: sA,
      scoreB: sB,
      winningTeam: winningTeam,
      timestamp: new Date().toISOString(),
    });

    var updatedGroups = groups.map(function (g) {
      if (g.court !== group.court) return g;
      return {
        ...g,
        matchIndex: g.matchIndex + 1,
        results: g.results.concat([{ teamA: m.teamA, teamB: m.teamB, winningTeam: winningTeam, scoreA: sA, scoreB: sB }]),
      };
    });
    updateDoc(doc(db, "liveSessions", sessionId), { groups: updatedGroups });
    setScoreInputs(function (prev) { return { ...prev, [group.court]: ["", ""] }; });
  }

  if (!groups) {
    var standingsPreview = computeStandings(history, players, names);
    return (
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Generate League Groups</h3>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: colors.textDim, marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Rank Players By</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={function () { setRankBy("allTime"); }} style={rankBy === "allTime" ? btnStyle : btnSecondaryStyle}>All-Time Results</button>
            <button onClick={function () { setRankBy("session"); }} style={rankBy === "session" ? btnStyle : btnSecondaryStyle}>This Session's History</button>
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          {standingsPreview.map(function (s) {
            return (
              <div key={s.uid} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px solid " + colors.border }}>
                <span>{s.name}</span>
                <span style={{ color: colors.textDim }}>{s.games > 0 ? Math.round(s.winPct * 100) + "% (" + s.wins + "-" + s.losses + ")" : "no games yet"}</span>
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: 12, color: colors.textDim }}>
          {players.length + " players → " + live.courtCount + " court(s), doubles. Top-ranked players form the first group(s); any extra players land in the top group. Each court group needs at least 4 players."}
        </p>
        <button style={btnStyle} onClick={generateGroups}>Generate Groups</button>
      </div>
    );
  }

  return (
    <div>
      {groups.map(function (g) {
        var tooSmall = g.players.length < 4;
        var done = !tooSmall && g.matchIndex >= g.matchOrder.length;
        var current = !tooSmall && !done ? g.matchOrder[g.matchIndex] : null;
        var s = scoreInputs[g.court] || ["", ""];
        var groupStandings = computeStandings(g.results, g.players, names);
        return (
          <div key={g.court} style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{"Court " + g.court}</div>
              {!tooSmall && <div style={{ fontSize: 12, color: colors.textDim }}>{g.matchIndex + "/" + g.matchOrder.length + " matches"}</div>}
            </div>

            {tooSmall && (
              <div style={{ fontSize: 13, color: colors.danger }}>{"Only " + g.players.length + " player(s) here — need at least 4 for doubles."}</div>
            )}

            {current && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1, fontSize: 14 }}>{teamName(current.teamA)}</div>
                <input style={{ ...inputStyle, width: 60 }} type="number" value={s[0]} onChange={function (e) {
                  setScoreInputs(function (prev) { return { ...prev, [g.court]: [e.target.value, s[1]] }; });
                }} />
                <span style={{ color: colors.textDim }}>vs</span>
                <input style={{ ...inputStyle, width: 60 }} type="number" value={s[1]} onChange={function (e) {
                  setScoreInputs(function (prev) { return { ...prev, [g.court]: [s[0], e.target.value] }; });
                }} />
                <div style={{ flex: 1, fontSize: 14, textAlign: "right" }}>{teamName(current.teamB)}</div>
              </div>
            )}
            {current && (
              <button style={btnSecondaryStyle} onClick={function () { submitMatch(g); }}>Submit Result</button>
            )}
            {done && (
              <div style={{ fontSize: 13, color: colors.success, marginBottom: 8 }}>{"All matches complete for this group"}</div>
            )}

            {!tooSmall && (
              <div style={{ marginTop: 12, borderTop: "1px solid " + colors.border, paddingTop: 10 }}>
                {groupStandings.map(function (row) {
                  return (
                    <div key={row.uid} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0" }}>
                      <span>{row.name}</span>
                      <span style={{ color: colors.textDim }}>{row.wins}W - {row.losses}L</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
